import React, { useEffect, useState } from 'react';
import { ReactComponent as RiderIcon } from 'assets/icons/motorbike.svg';
import { ReactComponent as CurrencyIcon } from 'assets/icons/coin.svg';
import { ReactComponent as LocationIcon } from 'assets/icons/location.svg';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { BaseModal, DateRangeType, Paginate } from '@cokitchen/cokitchen-components';
import { OrderType } from 'models/order.model';
import { apiService } from 'utils/api';
import { formatNumber, getOrderStatus, getSimpleDateFormat, getUserName } from 'utils/functions';
import OrderStatus from 'components/base/order-status';
import OrderDetail from 'pages/order-details';

const baseUrl = process.env.REACT_APP_ORDER_STATS_API_KEY;
const RecentOrderCard = ({ user, dateRange }: { user: any; dateRange: DateRangeType }) => {
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeOrder, setActiveOrder] = useState<OrderType>();
  const [showOrderModal, setShowOrderModal] = useState(false);

  const getOrders = async (count = 0) => {
    const pageNo = count + 1;
    setLoading(true);
    const res = await apiService.post(
      `user/one/orders/${pageNo}?start=${dateRange?.date?.startDate.toISOString()}&end=${dateRange?.date?.endDate?.toISOString()}`,
      {
        baseUrl,
        data: {
          user_id: user?.id
        }
      }
    );
    setLoading(false);
    setTotal(res.data?.data?.total);
    setOrders(res.data?.data?.data);
  };
  useEffect(() => {
    if (user) {
      getOrders();
    }
  }, []);

  useEffect(() => {
    setShowOrderModal(!!activeOrder);
    if (activeOrder) {
      searchParams.set('order', activeOrder.order_code);
      searchParams.set('orderStatus', getOrderStatus(activeOrder));
    } else {
      if (searchParams.has('order')) {
        searchParams.delete('order');
        searchParams.delete('orderStatus');
      }
    }
    setSearchParams(searchParams);
  }, [activeOrder]);

  return (
    <div className='mb-6'>
      {loading ? (
        <p className='text-center my-10 text-xl'>Loading user recent orders</p>
      ) : !loading && orders.length === 0 ? (
        <p className='text-center my-10 text-xl'>User has no order</p>
      ) : (
        <div className='grid md:grid-cols-2 gap-6'>
          {orders?.map((item) => (
            <div
              onClick={() => {
                setActiveOrder(item);
              }}
              key={item.order_code}
              className='text-[#595959] border py-3 cursor-pointer border-gray-2 rounded-lg flex flex-col'>
              <div className='flex justify-between text-[#1A1A1A] px-3'>
                <div className='flex flex-col gap-1'>
                  <OrderStatus type={getOrderStatus(item)} />
                  <p>{`#${item?.order_code?.toUpperCase() ?? ''}`}</p>
                </div>
                <p>{formatNumber(item?.calculated_order?.total_amount)}</p>
              </div>
              <div className='flex flex-col gap-2 grow px-3 mb-3 mt-2'>
                <div className='flex justify-between gap-1 flex-wrap'>
                  <p className='flex gap-2 items-center'>
                    <RiderIcon className='min-w-[16px]' />
                    {item.pickup ? 'Pickup' : getUserName(item.rider, 'No rider yet')}
                  </p>
                  <p className='flex gap-2 items-center text-[#1A1A1A]'>
                    <CurrencyIcon />
                    {formatNumber(item?.calculated_order?.delivery_fee)}
                  </p>
                </div>
                <p className='flex gap-2'>
                  <LocationIcon className='mt-2 min-w-[16px]' />
                  {item?.calculated_order?.address_details.address_line}
                </p>
              </div>
              <hr className='text-[#DCE2EA] pt-3' />
              <div className='flex px-4 text-sm justify-between'>
                <p>{getSimpleDateFormat(item.created_at)}</p>
                <p>{moment(item.created_at).format('hh:mm a')}</p>
                <p>
                  {item.completed_time ? (
                    moment(item.completed_time).format('hh:mm a')
                  ) : (
                    <span className='text-yellow-5'>In progress</span>
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      <Paginate onPageChange={(e) => getOrders(e.selected)} pageCount={Math.ceil(total / 10)} />

      <BaseModal
        position='right'
        isOpen={showOrderModal}
        onRequestClose={() => setActiveOrder(undefined)}
        size='wide'
        title={
          <div className='flex flex-wrap lg:flex-nowrap gap-2 items-center'>
            <button aria-label='Go back Icon' onClick={() => setActiveOrder(undefined)}>
              <ArrowLeftIcon className='w-6 h-6' />
            </button>
            <div className='font-semibold text-2xl'>
              Order #{activeOrder?.order_code?.toUpperCase()}
            </div>

            {activeOrder && (
              <div className='basis-full lg:basis-auto'>
                <OrderStatus type={getOrderStatus(activeOrder)} />
              </div>
            )}
          </div>
        }>
        {activeOrder && <OrderDetail orderCode={activeOrder?.order_code} />}
      </BaseModal>
    </div>
  );
};

export default RecentOrderCard;
